import { packingListContainerKey } from '../packingListContainer';
import { formatHsSummaryLine } from '../packingListSummaryText';
import { invoiceNumberCorrelativo } from '../../invoice/invoiceNumberUtils';

function safe(s) {
  return s == null ? '' : String(s);
}

function normalizeCountryOrigin(value) {
  const t = safe(value).trim();
  if (!t) return '';
  return /^brasil$/i.test(t) ? 'BRAZIL' : t.toUpperCase();
}

function hsCodeLinePkDecla(hsCode) {
  const raw = safe(hsCode).trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 6) {
    return `HS CODE : ${digits.slice(0, 4)}.${digits.slice(4)}`;
  }
  const formatted = formatHsSummaryLine(hsCode).replace(/\s+/g, '.');
  return formatted ? `HS CODE : ${formatted}` : `HS CODE : ${raw}`;
}

/** Contenedores distintos en orden de aparición (columna Container del packing). */
export function uniqueContainersFromPackingLineas(lineas) {
  const seen = new Set();
  const out = [];
  for (const row of lineas || []) {
    const k = packingListContainerKey(row).trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

export function merchandiseLinesForPkDecla(base) {
  const desc =
    safe(base.productDescription).trim().replace(/\s+/g, ' ').toUpperCase() ||
    safe(base.poLabel).trim().toUpperCase();
  let line2 = safe(base.lugarEntrega).trim().toUpperCase();
  if (!line2) {
    const parts = [safe(base.nombrePuertoDestino).trim(), normalizeCountryOrigin(base.paisDestino)]
      .filter(Boolean)
      .join(', ');
    line2 = parts.toUpperCase();
  }
  const hsLine = hsCodeLinePkDecla(base.hsCode);
  const country = normalizeCountryOrigin(base.paisOrigen);
  const countryLine = country ? `COUNTRY OF ORIGIN : ${country}` : '';
  return [desc, line2, hsLine, countryLine].filter(Boolean);
}

export function consigneeAddressLines(base) {
  const name = safe(base.clienteNombre).trim();
  const raw = safe(base.clienteDireccion).trim();
  const lines = raw ? raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean) : [];
  const cp = safe(base.clienteCodigoPostal).trim();
  if (cp && !lines.some((l) => /pin\s*code/i.test(l))) {
    lines.push(`PIN CODE: ${cp}`);
  }
  return { name, addressLines: lines };
}

/**
 * @param {object} api — GET /packinglists/:id
 * @param {{ numeroInvoice?: string }} [opts] — fila tabla invoice (puede traer número sin estar en api)
 */
function packingBaseFromApi(api) {
  if (!api) return {};
  return {
    numeroInvoice: api.numero_invoice || '',
    fechaEmbarque: api.fecha_embarque || '',
    clienteNombre: api.cliente_nombre_snapshot || '',
    clienteDireccion: api.cliente_direccion_snapshot || '',
    clienteCodigoPostal: api.cliente_codigo_postal || '',
    poLabel: api.po_label_snapshot || '',
    paisOrigen: api.pais_origen || '',
    paisDestino: api.pais_destino || '',
    nombreNave: api.nombre_nave || '',
    nombrePuertoOrigen: api.nombre_puerto_origen || '',
    nombrePuertoDestino: api.nombre_puerto_destino || '',
    lugarEntrega: api.lugar_entrega || '',
    nombreBl: api.nombre_bl || '',
    detalleExcel: api.lineas || [],
    productDescription: api.product_description_snapshot || '',
    hsCode: api.hs_code_snapshot || '',
  };
}

export function buildPkDeclaParams(api, opts = {}) {
  const base = packingBaseFromApi(api);
  const numero =
    invoiceNumberCorrelativo(opts.numeroInvoice || api?.numero_invoice || '') || '';
  const { name, addressLines } = consigneeAddressLines(base);
  return {
    ...base,
    numeroInvoice: numero,
    vesselName: safe(opts.vesselName || base.nombreNave).trim(),
    consigneeName: name,
    consigneeAddressLines: addressLines,
    containers: uniqueContainersFromPackingLineas(api?.lineas || base.detalleExcel),
    blNumber: safe(base.nombreBl).trim(),
    merchandiseLines: merchandiseLinesForPkDecla(base),
    issueDateIso: safe(base.fechaEmbarque).slice(0, 10),
  };
}
